import React, { Component, createRef, Dispatch } from 'react';
import { connect } from 'react-redux';
import { Grid } from '@material-ui/core';
import {
    SofaSelect,
    SofaInput,
    SofaCheckbox,
    SofaFormControl,
    AbstractSofaForm,
    checkboxValueToBooleanFilter,
    checkboxRequiredValidator,
    InputType,
    SofaFormWithGtm,
    SofaFormControlDescriptionWithGtm,
    getPropsForSofaInput,
    SofaFormFilter,
    SofaFormValidator,
    PostcodeModel
} from '@sofology/sofologycomponents';

import './DeliveryDetailsForm.scss';

import { AddressModel, areAddressesEqual, DeliveryDetailsModel, IfcAddressDescription, IlpQuestionDescriptions, SelectPropertyDescription, StringPropertyDescription } from 'models';
import { BasketErrorModel, BasketHybrisFormErrorModel } from 'models/Errors';
import { BasketDetailsModel } from 'models/Basket';
import { SofaFormComponent } from 'interfaces';
import { Loader } from 'components';
import { AppState } from 'store';
import {
    PRIVACY_POLICY_REQUIRED_ERROR_MESSAGE,
    PHONE_ME_TITLE,
    OFFER_ME_TITLE,
    TERM_AND_CONDITIONS_REQUIRED_ERROR_MESSAGE
} from './deliveryDetailsAdditionalData';
import { analyticsHelper, errorToBasketErrorModel, generateDispatchToPropsMap, paymentHelper } from 'helpers';
import {
    loadDeliveryDetails,
    LoadDeliveryDetailsFunc,
    updateDeliveryDetailsContactOptions,
    UpdateDeliveryDetailsContactOptionsFunc
} from 'store/checkout/actions';
import { DeliveryDetailsFormData } from 'models/Forms';
import DeliveryAddressForm, { DeliveryAddressFormType } from './DeliveryAddressForm/DeliveryAddressForm';
import { PaymentTypes } from 'models/Payment';
import BillingAddressForm, { BillingAddressFormType } from './BillingAddressForm/BillingAddressForm';
import { getControlFilters, getOptionsForSofaInput, getStringControlValidators, nullableValueToString, SofologyComponentsToIlpQuestionaryNames } from 'helpers/dynamicFormHelpers';

const GTM_FORM_NAME = 'deliveryDetails';

interface OwnProps {
    ref?: React.RefObject<DeliveryDetailsFormType>;
}

interface StateProps {
    deliveryDetails: DeliveryDetailsModel | null;
    isDeliveryDetailsLoading: boolean;
    basket: BasketDetailsModel;
    deliveryDetailsUpdatingErrors: BasketErrorModel[] | null;
    paymentType: PaymentTypes;
    ifcQuestionSet: IlpQuestionDescriptions | null;
}

interface DispatchProps {
    updateDeliveryDetailsContactOptions: UpdateDeliveryDetailsContactOptionsFunc;
    loadDeliveryDetails: LoadDeliveryDetailsFunc;
}

interface DeliveryDetailsControls extends AbstractSofaForm {
    title: SofaFormControl<string>;
    firstName: SofaFormControl<string>;
    surname: SofaFormControl<string>;
    phone: SofaFormControl<string>;
    allowPhoneContacts: SofaFormControl<boolean>;
    allowEmailContacts: SofaFormControl<boolean>;
    confirmPrivacyPolicyCustJourney: SofaFormControl<boolean>;
    confirmTermsAndConditions: SofaFormControl<boolean>;
}

interface OwnState {
    customerInfoForm: SofaFormWithGtm<DeliveryDetailsControls>;
    deliveryAddress: AddressModel | null;
    isPageInitialized: boolean;
}

class DeliveryDetailsForm extends Component<OwnProps & StateProps & DispatchProps, OwnState> implements SofaFormComponent<DeliveryDetailsFormData> {
    private deliveryAddressForm = React.createRef<DeliveryAddressFormType>();
    private billingAddressForm = React.createRef<BillingAddressFormType>();

    constructor(props: OwnProps & StateProps & DispatchProps) {
        super(props);

        this.state = {
            deliveryAddress: null,
            isPageInitialized: false,
            customerInfoForm: new SofaFormWithGtm<DeliveryDetailsControls>(
                this.createFormControlDescriptions(props),
                updateStateFunc => {
                    this.setState(updateStateFunc);
                },
                'customerInfoForm',
                () => this.state,
                GTM_FORM_NAME,
                analyticsHelper.sendToGoogleMessageAboutInputChangeCompleted.bind(analyticsHelper),
                errorToBasketErrorModel
            )
        };
    }

    componentDidMount() {
        this.props.loadDeliveryDetails();
    }

    componentDidUpdate(prevProps: OwnProps & StateProps & DispatchProps, prevState: OwnState) {
        if (this.props.deliveryDetails !== prevProps.deliveryDetails && this.props.deliveryDetails) {
            this.fillFormFromProps(this.props.deliveryDetails);
            this.setState({ deliveryAddress: this.props.deliveryDetails.deliveryAddress });
        }

        if (prevState.customerInfoForm.controls.allowEmailContacts.value !== this.state.customerInfoForm.controls.allowEmailContacts.value) {
            this.props.updateDeliveryDetailsContactOptions({ type: 'email', isAllowed: this.state.customerInfoForm.controls.allowEmailContacts.value });
        }

        if (prevState.customerInfoForm.controls.allowPhoneContacts.value !== this.state.customerInfoForm.controls.allowPhoneContacts.value) {
            this.props.updateDeliveryDetailsContactOptions({ type: 'phone', isAllowed: this.state.customerInfoForm.controls.allowPhoneContacts.value });
        }

        if (prevProps.isDeliveryDetailsLoading && !this.props.isDeliveryDetailsLoading) {
            this.setState({ isPageInitialized: true });
        }
    }

    handleDeliveryAddressChanged = (address: AddressModel) => {
        if (!areAddressesEqual(this.state.deliveryAddress, address)) {
            this.setState({ deliveryAddress: address });
        }
    };

    render(): JSX.Element {
        return (
            <div className='delivery-details-form'>
                {this.isFormLoading()
                    ? <Loader show={true} />
                    : <>
                        {this.renderCustomerDetailsForm()}
                        <div className='validation-scroll-container'>
                            <div className='delivery-details-form__title'>Delivery address</div>
                            <DeliveryAddressForm
                                defaultAddress={this.props.deliveryDetails?.deliveryAddress ?? null}
                                deliveryDetailsUpdatingErrors={this.props.deliveryDetailsUpdatingErrors}
                                onAddressChanged={this.handleDeliveryAddressChanged}
                                paymentType={this.props.paymentType}
                                ref={this.deliveryAddressForm}
                                ifcAddressQuestionSet={this.props.ifcQuestionSet!.address.properties.deliveryAddress as unknown as IfcAddressDescription}
                                fixedPostcode={this.props.basket.deliveryAddress!.postcode.code}
                            />
                            <hr />
                        </div>
                        <div className='validation-scroll-container'>
                            <div className='delivery-details-form__title'>Billing address</div>
                            <BillingAddressForm
                                defaultAddress={this.props.deliveryDetails?.billingAddress ?? null}
                                deliveryDetailsUpdatingErrors={this.props.deliveryDetailsUpdatingErrors}
                                paymentType={this.props.paymentType}
                                deliveryAddress={this.state.deliveryAddress}
                                ref={this.billingAddressForm}
                                ifcAddressQuestionSet={this.props.ifcQuestionSet!.address.properties.currentAddress as unknown as IfcAddressDescription}
                            />
                        </div>
                    </>
                }
            </div>
        );
    }

    renderCustomerDetailsForm(): JSX.Element {
        return (
            <div className='delivery-details-form__customer-details-form'>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <SofaSelect
                            label='Title *'
                            {...getPropsForSofaInput(this.state.customerInfoForm.controls.title)}
                            items={getOptionsForSofaInput((this.props.ifcQuestionSet!.customer.properties.title as SelectPropertyDescription).options)}
                            value={nullableValueToString(this.state.customerInfoForm.controls.title.value)}
                            helperText={this.correctErrorMessages(this.state.customerInfoForm.controls.title.errorMessage)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SofaInput
                            label='First Name *'
                            inputProps={{ autoCorrect: 'off', autoComplete: 'given-name' }}
                            {...getPropsForSofaInput(this.state.customerInfoForm.controls.firstName)}
                            helperText={this.correctErrorMessages(this.state.customerInfoForm.controls.firstName.errorMessage)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SofaInput
                            label='Surname *'
                            inputProps={{ autoCorrect: 'off', autoComplete: 'family-name' }}
                            {...getPropsForSofaInput(this.state.customerInfoForm.controls.surname)}
                            helperText={this.correctErrorMessages(this.state.customerInfoForm.controls.surname.errorMessage)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SofaInput
                            label='Contact telephone number *'
                            inputProps={{ type: 'tel', autoCorrect: 'off', inputMode: 'tel', autoComplete: 'tel' }}
                            {...getPropsForSofaInput(this.state.customerInfoForm.controls.phone)}
                            helperText={this.correctErrorMessages(this.state.customerInfoForm.controls.phone.errorMessage)}
                        />
                    </Grid>
                    <Grid item xs={12} className='delivery-details-form__checkbox-wrapper'>
                        <SofaCheckbox
                            label={PHONE_ME_TITLE}
                            {...getPropsForSofaInput(this.state.customerInfoForm.controls.allowPhoneContacts)}
                        />
                    </Grid>
                    <Grid item xs={12} className='delivery-details-form__checkbox-wrapper'>
                        <SofaCheckbox
                            label={OFFER_ME_TITLE}
                            {...getPropsForSofaInput(this.state.customerInfoForm.controls.allowEmailContacts)}
                        />
                    </Grid>
                    <Grid item xs={12} className='delivery-details-form__checkbox-wrapper delivery-details-form__checkbox-wrapper--link'>
                        <SofaCheckbox
                            label={
                                <span>I have read and understood the Sofology <a href='https://www.sofology.co.uk/privacy-policy' target='blank'>Privacy Policy</a>. *</span>
                            }
                            {...getPropsForSofaInput(this.state.customerInfoForm.controls.confirmPrivacyPolicyCustJourney)}
                        />
                    </Grid>
                    <Grid item xs={12} className='delivery-details-form__checkbox-wrapper delivery-details-form__checkbox-wrapper--link'>
                        <SofaCheckbox
                            label={
                                <span>I confirm I have read and agree to the Sofology <a href='https://www.sofology.co.uk/terms-and-conditions' target='blank'>Terms & Conditions</a>. *</span>

                            }
                            {...getPropsForSofaInput(this.state.customerInfoForm.controls.confirmTermsAndConditions)}
                        />
                    </Grid>
                </Grid>
            </div>
        );
    }

    public validate = (): Promise<SofaFormControl<any>[]> => {
        let validationPromises = [];

        validationPromises.push(this.state.customerInfoForm.validate());
        validationPromises.push(this.deliveryAddressForm.current!.validate());
        validationPromises.push(this.billingAddressForm.current!.validate());

        return Promise.all(validationPromises)
            .then(validationResults => {
                let invalidControls: SofaFormControl<any>[] = [];

                validationResults.forEach((formValidationResult: SofaFormControl<any>[]) => {
                    invalidControls.push(...formValidationResult);
                });

                return invalidControls;
            });
    };

    public getFormData = (): DeliveryDetailsFormData => {
        // trim() is called to prevent strings from having trailing spaces
        return {
            title: this.state.customerInfoForm.controls.title.value,
            firstName: this.state.customerInfoForm.controls.firstName.value.trim(),
            surname: this.state.customerInfoForm.controls.surname.value.trim(),
            phone: this.state.customerInfoForm.controls.phone.value.trim(),
            allowEmailContacts: this.state.customerInfoForm.controls.allowEmailContacts.value,
            allowPhoneContacts: this.state.customerInfoForm.controls.allowPhoneContacts.value,
            deliveryAddress: this.deliveryAddressForm.current!.getFormData(),
            billingAddress: this.billingAddressForm.current!.getFormData()
        };
    };

    fillFormFromProps(deliveryDetails: DeliveryDetailsModel) {
        const possibleTitleOptions = getOptionsForSofaInput((this.props.ifcQuestionSet!.customer.properties.title as SelectPropertyDescription).options);
        if (possibleTitleOptions.find(o => o.value === deliveryDetails.title)) {
            this.state.customerInfoForm.controls.title.setValue(deliveryDetails.title);
        }

        this.state.customerInfoForm.controls.firstName.setValue(deliveryDetails.firstName);
        this.state.customerInfoForm.controls.surname.setValue(deliveryDetails.surname);
        this.state.customerInfoForm.controls.phone.setValue(deliveryDetails.phone);
        this.state.customerInfoForm.controls.allowEmailContacts.setValue(deliveryDetails.allowEmailContacts);
        this.state.customerInfoForm.controls.allowPhoneContacts.setValue(deliveryDetails.allowPhoneContacts);
    }

    isFormLoading(): boolean {
        return (!this.state.isPageInitialized || this.props.isDeliveryDetailsLoading);
    }

    createFormControlDescriptions(props: OwnProps & StateProps): Record<keyof DeliveryDetailsControls, SofaFormControlDescriptionWithGtm<any>> {
        const defaultControlsDescriptions = {
            title: {
                name: 'title',
                defaultValue: '',
                ref: createRef<SofaSelect>(),
                inputType: InputType.Selector,
                gtmFieldName: 'customerTitle'
            } as SofaFormControlDescriptionWithGtm<string>,
            firstName: {
                name: 'firstName',
                defaultValue: '',
                ref: createRef<SofaInput>(),
                gtmFieldName: 'firstname'
            } as SofaFormControlDescriptionWithGtm<string>,
            surname: {
                name: 'surname',
                defaultValue: '',
                ref: createRef<SofaInput>(),
                gtmFieldName: 'lastname'
            } as SofaFormControlDescriptionWithGtm<string>,
            phone: {
                name: 'phone',
                defaultValue: '',
                ref: createRef<SofaInput>(),
                gtmFieldName: 'rawTelephone'
            } as SofaFormControlDescriptionWithGtm<string>,
            allowPhoneContacts: {
                name: 'allowPhoneContacts',
                defaultValue: false,
                filters: [checkboxValueToBooleanFilter()],
                validators: [],
                ref: createRef<SofaCheckbox>(),
                inputType: InputType.Checkbox,
                gtmFieldName: 'checkboxTel'
            } as SofaFormControlDescriptionWithGtm<boolean>,
            allowEmailContacts: {
                name: 'allowEmailContacts',
                defaultValue: false,
                filters: [checkboxValueToBooleanFilter()],
                validators: [],
                ref: createRef<SofaCheckbox>(),
                inputType: InputType.Checkbox,
                gtmFieldName: 'checkboxEmail'
            } as SofaFormControlDescriptionWithGtm<boolean>,
            confirmPrivacyPolicyCustJourney: {
                name: 'confirmPrivacyPolicyCustJourney',
                defaultValue: false,
                filters: [checkboxValueToBooleanFilter()],
                validators: [checkboxRequiredValidator(PRIVACY_POLICY_REQUIRED_ERROR_MESSAGE)],
                ref: createRef<SofaCheckbox>(),
                inputType: InputType.Checkbox,
                gtmFieldName: 'confirmPrivacyPolicyCustJourney'
            } as SofaFormControlDescriptionWithGtm<boolean>,
            confirmTermsAndConditions: {
                name: 'confirmTermsAndConditions',
                defaultValue: false,
                filters: [checkboxValueToBooleanFilter()],
                validators: [checkboxRequiredValidator(TERM_AND_CONDITIONS_REQUIRED_ERROR_MESSAGE)],
                ref: createRef<SofaCheckbox>(),
                inputType: InputType.Checkbox,
                gtmFieldName: 'confirmTermsAndConditions'
            } as SofaFormControlDescriptionWithGtm<boolean>,
        };

        const dataAccessObject = {};

        const questionSet = props.ifcQuestionSet!;

        dataAccessObject[SofologyComponentsToIlpQuestionaryNames.CustomerTitle] = () => this.state.customerInfoForm.controls.title.value;
        const titleQuestionProperty = questionSet.customer.properties.title as SelectPropertyDescription;
        defaultControlsDescriptions.title.validators = getStringControlValidators(titleQuestionProperty, dataAccessObject);

        dataAccessObject[SofologyComponentsToIlpQuestionaryNames.CustomerFirstName] = () => this.state.customerInfoForm.controls.firstName.value;
        const firstNameQuestionProperty = questionSet.customer.properties.forename as StringPropertyDescription;
        defaultControlsDescriptions.firstName.filters = getControlFilters(firstNameQuestionProperty) as SofaFormFilter<string>[];
        defaultControlsDescriptions.firstName.validators = getStringControlValidators(firstNameQuestionProperty, dataAccessObject);

        dataAccessObject[SofologyComponentsToIlpQuestionaryNames.CustomerSurname] = () => this.state.customerInfoForm.controls.surname.value;
        const surnameQuestionProperty = questionSet.customer.properties.surname as StringPropertyDescription;
        defaultControlsDescriptions.surname.filters = getControlFilters(surnameQuestionProperty) as SofaFormFilter<string>[];
        defaultControlsDescriptions.surname.validators = getStringControlValidators(surnameQuestionProperty, dataAccessObject);

        dataAccessObject[SofologyComponentsToIlpQuestionaryNames.CustomerPhone] = () => this.state.customerInfoForm.controls.phone.value;
        const phoneQuestionProperty = questionSet.address.properties.telephone as StringPropertyDescription;
        defaultControlsDescriptions.phone.filters = getControlFilters(phoneQuestionProperty) as SofaFormFilter<string>[];
        defaultControlsDescriptions.phone.validators = getStringControlValidators(phoneQuestionProperty, dataAccessObject);

        return defaultControlsDescriptions;
    }

    correctErrorMessages(errorMessage: string | null) {
        if (errorMessage === null) {
            return null;
        }

        if (errorMessage.includes('phone number must contain a minimum of')) {
            errorMessage.replace('phone number must contain a minimum of', 'Telephone number should be at least');
            errorMessage.replace('characters', 'digits');
        }

        switch (errorMessage) {
            case 'Please provide title':
                return 'Please provide your title';
            case 'Please provide first name':
                return 'Please provide your first name';
            case 'Please provide surname':
                return 'Please provide your last name';
            case 'Please provide phone number':
                return 'Please provide a contact telephone number';
            case 'Please provide a valid Phone number':
                return 'Please provide a valid telephone number';
            default:
                return errorMessage;
        }
    }

    postcodeEirValidator = (errorMessage: string): SofaFormValidator<string> => {
        return (value: string) => {
            let isInvalidPostcode = Boolean(
                paymentHelper.isCreditRelatedPaymentType(this.props.paymentType)
                && value.length > 0
                && (new PostcodeModel(value)).isEirCode);

            return Promise.resolve(isInvalidPostcode ? errorMessage : null);
        };
    };

    postcodeServerValidator = (errorMessage: string): SofaFormValidator<string> => {
        return (value: string, isChangedAfterExternalValidation: boolean) => {
            let isInvalidPostcode = Boolean(
                !isChangedAfterExternalValidation
                && this.props.deliveryDetailsUpdatingErrors
                && this.props.deliveryDetailsUpdatingErrors.find(e =>
                    e instanceof BasketHybrisFormErrorModel
                    && e.subject === 'postalCode'
                    && e.type === 'ValidationError'
                    && e.reason === 'invalid'
                    && e.formName === 'deliveryDetails'));

            return Promise.resolve(isInvalidPostcode ? errorMessage : null);
        };
    };
}

function mapStateToProps(state: AppState): StateProps {
    return {
        deliveryDetails: state.checkoutState.deliveryDetails,
        isDeliveryDetailsLoading: state.checkoutState.isDeliveryDetailsLoading,
        basket: state.basketState.basket!,
        deliveryDetailsUpdatingErrors: state.checkoutState.deliveryDetailsUpdatingErrors,
        paymentType: state.paymentState.paymentDetails.paymentType,
        ifcQuestionSet: state.checkoutState.ifcQuestionSet,
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [
        loadDeliveryDetails,
        updateDeliveryDetailsContactOptions
    ]);
}

export default connect(mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true })(DeliveryDetailsForm);
export type DeliveryDetailsFormType = DeliveryDetailsForm;